import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { GateDefectsCard } from "@/components/executive/GateDefectsCard";
import type { GateDefectsState } from "@/hooks/useGateDefects";
import type { CostPerItemState } from "@/hooks/useCostPerItem";

interface ExecHeroRowProps {
  gateDefects: GateDefectsState;
  costPerItem: CostPerItemState;
  portfolioAttentionCount: number;
}

function CostPerItemCard({ state }: { state: CostPerItemState }) {
  const { t } = useTranslation();
  const { result, planGated, error } = state;

  return (
    <Card data-testid="cost-per-item-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("executive.costPerItem.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {planGated ? (
          <p className="text-sm text-muted-foreground">
            {t("executive.costPerItem.unavailable")}
          </p>
        ) : error ? (
          <p className="text-sm text-muted-foreground">
            {t("executive.costPerItem.loadError")}
          </p>
        ) : result.distinctItems === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("executive.costPerItem.empty")}
          </p>
        ) : (
          <>
            <p className="text-2xl font-semibold">
              ${result.avgCostPerItem.toFixed(2)}{" "}
              <span className="text-sm font-normal text-muted-foreground">
                {t("executive.costPerItem.caption")}
              </span>
            </p>
            <div className="space-y-0.5 text-xs text-muted-foreground">
              <p>{t("executive.costPerItem.stories", { count: result.stories })}</p>
              {result.bugfixes > 0 && (
                <p>{t("executive.costPerItem.bugfixes", { count: result.bugfixes })}</p>
              )}
              {result.sessionFallbackPct > 0 && (
                <p>
                  {t("executive.costPerItem.fallback", {
                    pct: result.sessionFallbackPct.toFixed(0),
                  })}
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PortfolioSummaryCard({
  attentionCount,
}: {
  attentionCount: number;
}) {
  const { t } = useTranslation();

  return (
    <Card data-testid="portfolio-summary-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("executive.portfolio.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {attentionCount > 0 ? (
          <p className="text-2xl font-semibold">
            {attentionCount}{" "}
            <span className="text-sm font-normal text-muted-foreground">
              {t("executive.portfolio.attention")}
            </span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("executive.portfolio.healthy")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function PlaceholderCard() {
  const { t } = useTranslation();

  return (
    <Card
      data-testid="hero-placeholder-card"
      className="border-dashed opacity-60"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("executive.hero.placeholderTitle")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          {t("executive.hero.placeholderBody")}
        </p>
      </CardContent>
    </Card>
  );
}

export function ExecHeroRow({
  gateDefects,
  costPerItem,
  portfolioAttentionCount,
}: ExecHeroRowProps) {
  return (
    <div
      data-testid="exec-hero-row"
      className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
    >
      <GateDefectsCard state={gateDefects} />
      <CostPerItemCard state={costPerItem} />
      <PortfolioSummaryCard attentionCount={portfolioAttentionCount} />
      <PlaceholderCard />
    </div>
  );
}
